import React from "react";
import { useMemo } from "react";
import tinycolor from "tinycolor2";
import { contrast, hex, rgb, shade, tint } from "../../utils/colors";
export const systemFontStack = `-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"`;
const SHADOW = 18;
const colorTypes = [
    "primary",
    "secondary",
    "tertiary",
    "success",
    "warning",
    "danger",
    "dark",
    "medium",
    "light",
];
/**
 * Internally used in AdaptiveUi to render theme styles.
 */
export function AdaptiveUiThemeStyle({ theme, selector = "body", }) {
    const css = useMemo(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z;
        const colors = colorTypes
            .map((key) => {
            var _a;
            const c = theme[key];
            let shades;
            if (typeof c === "string") {
                const color = hex(c);
                shades = {
                    default: color,
                    shade: shade(color, 5, theme.invert),
                    dark: shade(color, 12, theme.invert),
                    darker: shade(color, 30, theme.invert),
                    tint: tint(color, 5, theme.invert),
                    light: tint(color, 12, theme.invert),
                    lighter: tint(color, 30, theme.invert),
                };
            }
            else {
                shades = c;
            }
            const contrastColor = hex((_a = theme[`${key}Contrast`]) !== null && _a !== void 0 ? _a : contrast(shades.default));
            return `
  --${key}: ${shades.default};
  --${key}-rgb: ${rgb(shades.default)};
  --${key}-shadow: ${shade(shades.default, SHADOW)};
  --${key}-shadow-rgb: ${rgb(shade(shades.default, SHADOW))};
  --${key}-shade: ${shades.shade};
  --${key}-shade-rgb: ${rgb(shades.shade)};
  --${key}-dark: ${shades.dark};
  --${key}-dark-rgb: ${rgb(shades.dark)};
  --${key}-darker: ${shades.darker};
  --${key}-darker-rgb: ${rgb(shades.darker)};
  --${key}-tint: ${shades.tint};
  --${key}-tint-rgb: ${rgb(shades.tint)};
  --${key}-light: ${shades.light};
  --${key}-light-rgb: ${rgb(shades.light)};
  --${key}-lighter: ${shades.lighter};
  --${key}-lighter-rgb: ${rgb(shades.lighter)};
  --${key}-contrast: ${contrastColor};
  --${key}-contrast-rgb: ${rgb(contrastColor)};
`;
        })
            .join("\n");
        const buttons = colorTypes
            .map((key) => {
            const c = theme[key];
            const lum = tinycolor(typeof c === "string" ? c : c.default).getLuminance();
            const isLighter = theme.invert ? lum < 0.2 : lum > 0.7;
            switch (theme.buttonStyle) {
                case "outline":
                    return `
  --btn-${key}-background: transparent;
  --btn-${key}-hover-background: rgba(var(--${key}-rgb), 0.1);
  --btn-${key}-text: var(--primary);
  --btn-${key}-icon: var(--${isLighter ? key + "-darker" : key});
  --btn-${key}-border-color: var(--${isLighter ? key + "-darker" : key});
`;
                case "outset":
                    return `
  --btn-${key}-background: var(--${key});
  --btn-${key}-hover-background: var(--${key}-${theme.invert ? "shade" : "tint"});
  --btn-${key}-text: var(--${key}-contrast);
  ${theme.invert
                        ? `--btn-${key}-border-color: var(--${key}-dark) var(--${key}-tint) var(--${key}-lighter) var(--${key}-shade);`
                        : `--btn-${key}-border-color: var(--${key}-light) var(--${key}-shade) var(--${key}-darker) var(--${key}-tint);`}
`;
                default:
                    return `
  --btn-${key}-background: var(--${key});
  --btn-${key}-hover-background: var(--${key}-${theme.invert ? "shade" : "tint"});
  --btn-${key}-text: var(--${key}-contrast);
  --btn-${key}-border-color: var(--${key}-darker);
`;
            }
        })
            .join("\n");
        return `
${selector} {
${colors}

  --white: ${theme.invert ? "#000" : "#fff"};
  --black: ${theme.invert ? "#fff" : "#000"};

  --default-background: ${theme.defaultBackground ? hex(theme.defaultBackground) : "var(--light)"};
  --default-text: ${theme.defaultTextColor
            ? hex(theme.defaultTextColor)
            : "var(--light-contrast)"};
  --default-spacing: ${(_a = theme.defaultSpacing) !== null && _a !== void 0 ? _a : 5}px;

  --default-font: ${(_b = theme.fontStack) !== null && _b !== void 0 ? _b : systemFontStack};
  --default-font-weight: ${(_c = theme.fontWeight) !== null && _c !== void 0 ? _c : 400};

  --overlay-background: rgba(var(--light-rgb), 0.7);

  --heading-font: ${(_d = theme.headingFontStack) !== null && _d !== void 0 ? _d : "var(--default-font)"};
  --heading-font-weight: ${(_e = theme.headingFontWeight) !== null && _e !== void 0 ? _e : 600};
  --heading-text: ${theme.headingTextColor
            ? hex(theme.headingTextColor)
            : "var(--light-contrast)"};

  ${((_f = theme.headingFontSizes) !== null && _f !== void 0 ? _f : [])
            .map((s, i) => `--heading-${i + 1}-font-size: ${s};`)
            .join("\n")}

  --label-font-weight: ${(_g = theme.labelFontWeight) !== null && _g !== void 0 ? _g : "var(--default-font-weight)"};
  --label-text: ${(_h = theme.labelTextColor) !== null && _h !== void 0 ? _h : "var(--default-text)"};

  --link-text: ${theme.linkTextColor ? hex(theme.linkTextColor) : "var(--primary)"};
  --link-hover-text: ${theme.linkTextColor ? tint(theme.linkTextColor) : "var(--primary-tint)"};

  --radius: ${(_j = theme.radius) !== null && _j !== void 0 ? _j : 0}px;

  --btn-radius: ${(_l = (_k = theme.buttonRadius) !== null && _k !== void 0 ? _k : theme.radius) !== null && _l !== void 0 ? _l : 0}px;
  ${theme.smallButtonRadius
            ? `--btn-small-radius: ${theme.smallButtonRadius}px`
            : ""};
  ${theme.largeButtonRadius
            ? `--btn-large-radius: ${theme.largeButtonRadius}px`
            : ""};
  --btn-gap: ${(_m = theme.buttonGap) !== null && _m !== void 0 ? _m : 5}px;
  --btn-padding: ${(_o = theme.buttonPadding) !== null && _o !== void 0 ? _o : ".75em"};
  --btn-small-padding: ${(_p = theme.smallButtonPadding) !== null && _p !== void 0 ? _p : ".9em"};
  --btn-large-padding: ${(_q = theme.largeButtonPadding) !== null && _q !== void 0 ? _q : ".6em"};
  --btn-border-width: ${Array.isArray(theme.buttonBorderWidth)
            ? theme.buttonBorderWidth.map((w) => w + "px").join(" ")
            : theme.buttonBorderWidth != undefined
                ? theme.buttonBorderWidth + "px"
                : "1px"};

  ${buttons}

  --input-radius: ${(_s = (_r = theme.inputRadius) !== null && _r !== void 0 ? _r : theme.radius) !== null && _s !== void 0 ? _s : 0}px;
  --input-padding: ${(_t = theme.inputPadding) !== null && _t !== void 0 ? _t : ".5em"};
  --input-border-width: ${Array.isArray(theme.inputBorderWidth)
            ? theme.inputBorderWidth.map((w) => w + "px").join(" ")
            : theme.inputBorderWidth != undefined
                ? theme.inputBorderWidth + "px"
                : "1px"};

  --input-border-width-single: ${theme.inputBorderWidthSingle != undefined
            ? theme.inputBorderWidthSingle
            : Array.isArray(theme.inputBorderWidth)
                ? theme.inputBorderWidth[0]
                : theme.inputBorderWidth != undefined
                    ? theme.inputBorderWidth
                    : 1}px;
  --input-background: ${theme.inputBackground ? hex(theme.inputBackground) : "var(--light-tint)"};
  --input-shadow: ${theme.inputBackground
            ? hex(shade(theme.inputBackground, SHADOW))
            : "var(--light-shadow)"};
  --input-shadow-offset: ${(_u = theme.inputShadowOffset) !== null && _u !== void 0 ? _u : "1px 1px"};
  --input-shadow-blur: ${(_v = theme.inputShadowBlur) !== null && _v !== void 0 ? _v : 3}px;
  --input-text: ${theme.inputTextColor
            ? hex(theme.inputTextColor)
            : theme.inputBackground
                ? contrast(theme.inputBackground)
                : "var(--light-contrast)"};
  --input-border-color: ${Array.isArray(theme.inputBorderColor)
            ? theme.inputBorderColor.map((c) => hex(c)).join(" ")
            : theme.inputBorderColor
                ? hex(theme.inputBorderColor)
                : "var(--light-darker)"};
  --input-border-color-single: ${theme.inputBorderColorSingle
            ? hex(theme.inputBorderColorSingle)
            : Array.isArray(theme.inputBorderColor)
                ? hex(theme.inputBorderColor[0])
                : theme.inputBorderColor
                    ? hex(theme.inputBorderColor)
                    : "var(--light-darker)"};
  --input-focus-background: ${theme.inputBackground
            ? tint(theme.inputBackground, 10)
            : "var(--light-lighter)"};
  --checkbox-radius: ${(_y = (_x = (_w = theme.checkboxRadius) !== null && _w !== void 0 ? _w : theme.inputRadius) !== null && _x !== void 0 ? _x : theme.radius) !== null && _y !== void 0 ? _y : 0}px;
  --checkbox-border-width: ${theme.checkboxBorderWidth !== undefined
            ? theme.checkboxBorderWidth + "px"
            : "var(--input-border-width-single)"};
  --option-padding: ${(_z = theme.optionPadding) !== null && _z !== void 0 ? _z : "12px"};

  --invert: ${theme.invert ? 1 : 0};

  ${!!theme.overrides &&
            Object.keys(theme.overrides)
                .map(
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            (key) => `${key}: ${theme.overrides[key]};`)
                .join("\n  ")}
}

${selector} ::-webkit-calendar-picker-indicator {
  filter: invert(var(--invert));
}

`;
    }, [theme, selector]);
    return React.createElement("style", null, css);
}
